import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getHierarchy, saveHierarchy, getHierarchyAffectedAccounts, cascadeUpdateHierarchy } from '../services/authentikAPI';
import toast, { Toaster } from 'react-hot-toast';
import { page, cardScroll, modalOverlay, modalCard, ConfirmDialog } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// UPDATED 2026-08-05: restyled onto the same light glass-card shell as
// AdminUsers.jsx/User Directory (page/card imported from there) — this
// used to have its own bespoke dark tinted-glass header bar + full-width
// dashboard layout, inconsistent with the rest of the admin section. The
// stat cards/tabs/tables below are unchanged, just now sitting inside that
// shared card instead of a plain padded div.

const inp = {
  width: '100%', padding: '9px 12px',
  border: '1.5px solid #D3D1C7', borderRadius: '8px',
  fontSize: '14px', boxSizing: 'border-box',
  color: '#2c2c2a', background: '#fff', outline: 'none'
};

const lbl = {
  display: 'block', fontSize: '12px',
  fontWeight: '500', color: '#5F5E5A', marginBottom: '4px'
};

const emptyForm = { zone: '', circle: '', division: '', subdivision: '', section: '' };

// Distinct existing values at each level, scoped to whatever's already
// picked above it (e.g. Circle's own list is only circles that actually
// exist under the currently-selected Zone) — so picking a value at one
// level narrows what's offered below it, same as the real tree, without
// ever forcing a cleared/reset field (see HierarchyFieldInput's own
// comment on why: this is an EDIT form, not a from-scratch wizard, and
// auto-clearing a correct child field just because an admin fixed a typo
// in its parent would be actively hostile).
function hierarchyFieldOptions(hierarchy, values) {
  const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
  return {
    zones: uniq(hierarchy.map((h) => h.zone)),
    circles: uniq(hierarchy.filter((h) => h.zone === values.zone).map((h) => h.circle)),
    divisions: uniq(hierarchy.filter((h) => h.zone === values.zone && h.circle === values.circle).map((h) => h.division)),
    subdivisions: uniq(hierarchy.filter((h) => h.zone === values.zone && h.circle === values.circle && h.division === values.division).map((h) => h.subdivision)),
    sections: uniq(hierarchy.filter((h) => h.zone === values.zone && h.circle === values.circle && h.division === values.division && h.subdivision === (values.subdivision || '')).map((h) => h.section)),
  };
}

// The COMPLETE Zone → Circle → Division → Sub-Division → Section path for
// one entry — used by the flat "All Sections" list.
function fullPath(h) {
  return `${h.zone} › ${h.circle} › ${h.division}${h.subdivision ? ' › ' + h.subdivision : ''} › ${h.section}`;
}

// The remaining path BELOW whichever level a statFilter popup row is
// expanding, down to Section — e.g. expanding a Zone row shows
// "Circle › Division › Sub-Division › Section" for each of its children,
// expanding a Sub-Division row shows just "Section".
function childPath(h, type) {
  switch (type) {
    case 'zone':
      return `${h.circle} › ${h.division}${h.subdivision ? ' › ' + h.subdivision : ''} › ${h.section}`;
    case 'circle':
      return `${h.division}${h.subdivision ? ' › ' + h.subdivision : ''} › ${h.section}`;
    case 'division':
      return `${h.subdivision ? h.subdivision + ' › ' : ''}${h.section}`;
    case 'subdivision':
    default:
      return h.section;
  }
}

// A plain text input backed by a <datalist> of existing values at this
// level — picking a suggestion fills the box same as typing; typing
// anything NOT in the list just creates a new value, no separate "Create
// new" mode/button needed. Same technique already shipped for User
// Directory's filter search boxes (AdminUsers.jsx).
function HierarchyFieldInput({ name, value, onChange, options, listId, placeholder, required }) {
  return (
    <>
      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        list={listId}
        autoComplete="off"
        style={inp}
      />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

export default function AdminHierarchy() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [hierarchy, setHierarchy] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('view'); // 'view' | 'add' | 'upload'
  const [csvPreview, setCsvPreview] = useState([]);
  const [statFilter, setStatFilter] = useState(null); // { type: 'zone'|'circle'|'division', title: '' }
  // Which item rows are expanded in the CURRENT statFilter popup — names,
  // not indices (stable across the same popup's own re-renders). Reset
  // whenever a stat card is clicked / the popup is closed, since these
  // are meaningless once `statFilter` itself changes to a different list.
  const [expandedStatRows, setExpandedStatRows] = useState(new Set());
  const toggleStatRow = (name) => {
    setExpandedStatRows((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  // A "standalone" entry (e.g. BESCOM Corporate Office, DAS BESCOM) is a
  // hierarchy row with only `zone` set and circle/division/subdivision/
  // section all blank. CompleteProfile.jsx already knows how to handle
  // this shape — its isZoneOffice check (a zone with no circle beneath it)
  // lets a user pick just that "zone" and submit without drilling further
  // — but until now there was no way to CREATE one from this page, since
  // handleAdd required all four of zone/circle/division/section.
  const [standalone, setStandalone] = useState(false);
  // Bulk delete on the View All tab — holds indices into `hierarchy`
  // (matching each row's own h._idx), not the filtered/grouped view, so
  // selection stays correct regardless of search/grouping.
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Guard: only bescom-super-admins. Two cases in one effect, not one
  // check each — checking the group only when `user` is already truthy
  // would silently do nothing for a visitor with NO session at all (a
  // gap this page and AdminAuditLog.jsx both used to have masked by a
  // separate localStorage-token check, removed as part of the httpOnly-
  // cookie migration — see AuthContext.js's own header comment). Save
  // itself was never actually at risk either way (hierarchy.js's PUT
  // route requires a real super-admin session), but the page shouldn't
  // render at all before confirming there's even one.
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      toast.error('Access denied. Super admins only.');
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  // Load hierarchy
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getHierarchy();
        setHierarchy(data);
      } catch (err) {
        toast.error('Failed to load hierarchy');
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();

    if (standalone) {
      const name = form.zone.trim();
      if (!name) {
        toast.error('Name is required');
        return;
      }
      const exists = hierarchy.some(h => h.zone === name && !h.circle);
      if (exists) { toast.error('This entry already exists'); return; }

      setSaving(true);
      try {
        const updated = [...hierarchy, { zone: name, circle: '', division: '', subdivision: '', section: '' }];
        await saveHierarchy(updated);
        setHierarchy(updated);
        setForm(emptyForm);
        toast.success('Entry added successfully!');
        setActiveTab('view');
      } catch (err) {
        toast.error('Failed to save');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!form.zone || !form.circle || !form.division || !form.section) {
      toast.error('Zone, Circle, Division and Section are required');
      return;
    }
    // Check duplicate
    const exists = hierarchy.some(h =>
      h.zone === form.zone && h.circle === form.circle &&
      h.division === form.division && h.subdivision === form.subdivision &&
      h.section === form.section
    );
    if (exists) { toast.error('This entry already exists'); return; }

    setSaving(true);
    try {
      const updated = [...hierarchy, { ...form }];
      await saveHierarchy(updated);
      setHierarchy(updated);
      setForm(emptyForm);
      toast.success('Entry added successfully!');
      setActiveTab('view');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Delete this entry?')) return;
    setSaving(true);
    try {
      const updated = hierarchy.filter((_, i) => i !== index);
      await saveHierarchy(updated);
      setHierarchy(updated);
      toast.success('Entry deleted');
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  // Edit an EXISTING entry's own fields, including the levels above it
  // (zone/circle/division/subdivision) — not just add new rows or delete
  // whole ones. Same flat-array model as everywhere else on this page: a
  // Section row carries its own full copy of zone/circle/division/
  // subdivision, so editing one row's parent chain only ever affects that
  // one row, same granularity Delete already has — not a bulk rename of
  // every other entry that happens to currently share the same parent.
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editStandalone, setEditStandalone] = useState(false);
  // Opt-in, unchecked by default — see hierarchy.js's own header comment
  // on /affected-accounts + /cascade-update for why this stays an
  // explicit choice rather than always cascading: it's a bulk rewrite of
  // other people's account data, and the admin should see the real count
  // before committing to it.
  const [cascadeUpdate, setCascadeUpdate] = useState(false);
  const [cascading, setCascading] = useState(false);
  // { title, message, confirmLabel, onConfirm } for ConfirmDialog, or null
  // to hide it — replaces a plain window.confirm() with the app's own
  // styled dialog (same component AdminApprovals.jsx/AdminGroupDetail.jsx
  // already use), consistent with the rest of the admin section instead
  // of a bare browser popup.
  const [cascadeConfirm, setCascadeConfirm] = useState(null);

  const openEdit = (h) => {
    setEditIdx(h._idx);
    setEditForm({
      zone: h.zone || '', circle: h.circle || '', division: h.division || '',
      subdivision: h.subdivision || '', section: h.section || '',
    });
    setEditStandalone(!h.circle);
    setCascadeUpdate(false);
  };

  const closeEdit = () => {
    setEditIdx(null);
    setEditForm(emptyForm);
    setEditStandalone(false);
    setCascadeUpdate(false);
  };

  const handleEditChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Shared by both paths below: straight through when there's nothing to
  // confirm (no cascade, or a cascade with zero affected accounts), and
  // from the ConfirmDialog's own onConfirm once there's something to
  // confirm.
  async function commitEdit(newEntry, oldEntry, doCascade) {
    setSaving(true);
    try {
      const updated = hierarchy.map((h, i) => (i === editIdx ? newEntry : h));
      await saveHierarchy(updated);
      setHierarchy(updated);

      if (doCascade) {
        const result = await cascadeUpdateHierarchy(oldEntry, newEntry);
        toast.success(`Entry updated. ${result.updated || 0} existing account${result.updated === 1 ? '' : 's'} also updated.`);
      } else {
        toast.success('Entry updated successfully!');
      }
      closeEdit();
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (editIdx === null) return;

    let newEntry;
    if (editStandalone) {
      const name = editForm.zone.trim();
      if (!name) { toast.error('Name is required'); return; }
      const exists = hierarchy.some((h, i) => i !== editIdx && h.zone === name && !h.circle);
      if (exists) { toast.error('This entry already exists'); return; }
      newEntry = { zone: name, circle: '', division: '', subdivision: '', section: '' };
    } else {
      if (!editForm.zone || !editForm.circle || !editForm.division || !editForm.section) {
        toast.error('Zone, Circle, Division and Section are required');
        return;
      }
      const exists = hierarchy.some((h, i) =>
        i !== editIdx && h.zone === editForm.zone && h.circle === editForm.circle &&
        h.division === editForm.division && h.subdivision === editForm.subdivision &&
        h.section === editForm.section
      );
      if (exists) { toast.error('This entry already exists'); return; }
      newEntry = { ...editForm };
    }

    const oldEntry = hierarchy[editIdx];

    // Preview BEFORE touching anyone's account — the count is only known
    // once this call returns, so this can't be folded into a single
    // "are you sure" shown ahead of the request. Confirmation itself is
    // the app's own ConfirmDialog (cascadeConfirm state below), not a
    // native window.confirm() popup.
    if (cascadeUpdate) {
      setCascading(true);
      let preview;
      try {
        preview = await getHierarchyAffectedAccounts(oldEntry);
      } catch (err) {
        setCascading(false);
        toast.error('Could not check affected accounts — entry not saved.');
        return;
      }
      setCascading(false);
      if (preview.count > 0) {
        setCascadeConfirm({
          title: 'Update existing accounts too?',
          message: `This will also update ${preview.count} existing account${preview.count === 1 ? '' : 's'} currently assigned to this exact entry (${preview.usernames.slice(0, 10).join(', ')}${preview.count > 10 ? ', …' : ''}), changing their posting to match the new values.`,
          confirmLabel: 'Update accounts',
          onConfirm: () => {
            setCascadeConfirm(null);
            commitEdit(newEntry, oldEntry, true);
          },
        });
        return;
      }
    }

    await commitEdit(newEntry, oldEntry, cascadeUpdate);
  };

  const toggleRowSelected = (index) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected entr${selectedRows.size === 1 ? 'y' : 'ies'}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const updated = hierarchy.filter((_, i) => !selectedRows.has(i));
      await saveHierarchy(updated);
      setHierarchy(updated);
      setSelectedRows(new Set());
      toast.success(`${selectedRows.size} entr${selectedRows.size === 1 ? 'y' : 'ies'} deleted`);
    } catch (err) {
      toast.error('Failed to delete selected entries');
    } finally {
      setSaving(false);
    }
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Handle Windows line endings
      const lines = ev.target.result
        .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
        .split('\n')
        .filter(l => l.trim() && !l.split(',').every(v => !v.trim()));

      const rawHeaders = lines[0].split(',').map(h => h.trim().toLowerCase());

      // Normalize header names
      const headerMap = {
        'zone': 'zone',
        'circle': 'circle',
        'division': 'division',
        'subdivision': 'subdivision',
        'subdivision name': 'subdivision',
        'sub-division': 'subdivision',
        'sub division': 'subdivision',
        'section': 'section',
        'section name': 'section',
      };
      const headers = rawHeaders.map(h => headerMap[h] || h);

      const rows = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim());
        const obj = {};
        headers.forEach((h, i) => obj[h] = vals[i] || '');
        return {
          zone: obj.zone || '',
          circle: obj.circle || '',
          division: obj.division || '',
          subdivision: obj.subdivision || '',
          section: obj.section || ''
        };
      }).filter(r => r.zone); // only need zone at minimum

      setCsvPreview(rows);
      toast.success(`${rows.length} valid entries ready to import`);
    };
    reader.readAsText(file);
  };

  const handleCSVImport = async () => {
    if (!csvPreview.length) return;
    setSaving(true);
    try {
      // CONFIRMED BUG (2026-09-30): this used to merge against the
      // `hierarchy` component state, loaded once on mount — fine if
      // nothing else changed the real data since this page was opened,
      // but a save (PUT /api/hierarchy) always replaces the WHOLE stored
      // array. An entry added via the Add tab's "Independent entry"
      // option (or from another tab/session) after this page's initial
      // load, but before a CSV import ran, would exist on the server yet
      // be invisible to this stale local copy — the import's own merge
      // would then silently drop it when it overwrote the server's array
      // with (stale local data + new CSV rows). Re-fetching immediately
      // before merging closes that window regardless of source/session.
      const latest = await getHierarchy();
      setHierarchy(latest);
      const merged = [...latest];
      let added = 0;
      for (const row of csvPreview) {
        const exists = merged.some(h =>
          h.zone === row.zone && h.circle === row.circle &&
          h.division === row.division && h.subdivision === (row.subdivision || '') &&
          h.section === row.section
        );
        if (!exists) { merged.push(row); added++; }
      }
      await saveHierarchy(merged);
      setHierarchy(merged);
      setCsvPreview([]);
      toast.success(`Imported ${added} new entries (${csvPreview.length - added} duplicates skipped)`);
      setActiveTab('view');
    } catch (err) {
      toast.error('Import failed');
    } finally {
      setSaving(false);
    }
  };

  const filtered = hierarchy.filter(h =>
    !search || Object.values(h).some(v =>
      String(v).toLowerCase().includes(search.toLowerCase())
    )
  );

  // Group for display — a standalone entry (see `standalone` above) has no
  // circle/division, so it gets its own single-row group keyed on just its
  // name rather than the usual "Zone > Circle > Division" grouping.
  const grouped = filtered.reduce((acc, h, idx) => {
    const key = h.circle ? `${h.zone} > ${h.circle} > ${h.division}` : h.zone;
    if (!acc[key]) acc[key] = [];
    acc[key].push({ ...h, _idx: hierarchy.indexOf(h) });
    return acc;
  }, {});
  // Flattened, _idx-bearing view of everything currently rendered — used
  // for "select all visible" so it selects the exact same rows the table
  // shows, not `filtered` (whose plain entries never got `_idx` attached).
  const visibleRows = Object.values(grouped).flat();

  // Suggestion lists for the Add form's own fields (see
  // hierarchyFieldOptions's own comment) and the Edit modal's, further
  // below — computed every render off the current hierarchy + whatever's
  // already picked, same as `filtered`/`grouped` above.
  const addOptions = hierarchyFieldOptions(hierarchy, form);
  const editOptions = hierarchyFieldOptions(hierarchy, editForm);

  if (loading || loadingData) {
    return (
      <>
        <TopBanner />
        <div className="admin-page-wrap" style={page}>
          <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
            <AdminSidebar active="hierarchy" />
            <div style={{ ...cardScroll, width: 'auto' }}>
              <p style={{ margin: 0, color: '#666' }}>Loading...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const tabBtn = (id, label) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '8px 20px', borderRadius: '8px', border: 'none',
        fontWeight: '600', fontSize: '14px', cursor: 'pointer',
        background: activeTab === id ? '#009FE3' : '#fff',
        color: activeTab === id ? '#fff' : '#555',
        boxShadow: activeTab === id ? '0 2px 8px rgba(0,159,227,0.3)' : 'none'
      }}>
      {label}
    </button>
  );

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
      <Toaster />
      <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
      <AdminSidebar active="hierarchy" />
      <div style={cardScroll}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
          Hierarchy Management
        </h1>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '24px' }}>
          Manage Zone → Circle → Division → Sub-Division → Section
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[
            {
              label: 'Zones', color: '#0F6E56',
              value: [...new Set(hierarchy.filter(h => h.section).map(h => h.zone))].length,
              onClick: () => {
                const zones = [...new Set(hierarchy.filter(h => h.section).map(h => h.zone))].filter(Boolean);
                setExpandedStatRows(new Set());
                setStatFilter({
                  type: 'zone',
                  title: 'All Zones',
                  color: '#0F6E56',
                  items: zones.map(z => ({
                    name: z,
                    count: hierarchy.filter(h => h.zone === z && h.section).length,
                    children: hierarchy.filter(h => h.zone === z && h.section)
                      .sort((a, b) => childPath(a, 'zone').localeCompare(childPath(b, 'zone')))
                  })).sort((a,b) => a.name.localeCompare(b.name))
                });
              }
            },
            {
              label: 'Circles', color: '#534AB7',
              value: [...new Set(hierarchy.filter(h => h.section).map(h => h.circle))].filter(Boolean).length,
              onClick: () => {
                const circles = [...new Set(hierarchy.filter(h => h.section && h.circle).map(h => h.circle))];
                setExpandedStatRows(new Set());
                setStatFilter({
                  type: 'circle',
                  title: 'All Circles',
                  color: '#534AB7',
                  items: circles.map(c => {
                    const sample = hierarchy.find(h => h.circle === c) || {};
                    return {
                      name: `${sample.zone} › ${c}`,
                      sortKey: c,
                      count: hierarchy.filter(h => h.circle === c && h.section).length,
                      children: hierarchy.filter(h => h.circle === c && h.section)
                        .sort((a, b) => childPath(a, 'circle').localeCompare(childPath(b, 'circle')))
                    };
                  }).sort((a,b) => a.sortKey.localeCompare(b.sortKey))
                });
              }
            },
            {
              label: 'Divisions', color: '#E67E22',
              value: [...new Set(hierarchy.filter(h => h.section).map(h => h.division))].filter(Boolean).length,
              onClick: () => {
                const divs = [...new Set(hierarchy.filter(h => h.section && h.division).map(h => h.division))];
                setExpandedStatRows(new Set());
                setStatFilter({
                  type: 'division',
                  title: 'All Divisions',
                  color: '#E67E22',
                  items: divs.map(d => {
                    const sample = hierarchy.find(h => h.division === d) || {};
                    return {
                      name: `${sample.zone} › ${sample.circle} › ${d}`,
                      sortKey: d,
                      count: hierarchy.filter(h => h.division === d && h.section).length,
                      children: hierarchy.filter(h => h.division === d && h.section)
                        .sort((a, b) => childPath(a, 'division').localeCompare(childPath(b, 'division')))
                    };
                  }).sort((a,b) => a.sortKey.localeCompare(b.sortKey))
                });
              }
            },
            {
              label: 'Sub-Divisions', color: '#E91E8C',
              value: [...new Set(hierarchy.filter(h => h.section).map(h => h.subdivision))].filter(Boolean).length,
              onClick: () => {
                const subs = [...new Set(hierarchy.filter(h => h.section && h.subdivision).map(h => h.subdivision))];
                setExpandedStatRows(new Set());
                setStatFilter({
                  type: 'subdivision',
                  title: 'All Sub-Divisions',
                  color: '#E91E8C',
                  items: subs.map(s => {
                    const sample = hierarchy.find(h => h.subdivision === s) || {};
                    return {
                      name: `${sample.zone} › ${sample.circle} › ${sample.division} › ${s}`,
                      sortKey: s,
                      count: hierarchy.filter(h => h.subdivision === s && h.section).length,
                      children: hierarchy.filter(h => h.subdivision === s && h.section)
                        .sort((a, b) => childPath(a, 'subdivision').localeCompare(childPath(b, 'subdivision')))
                    };
                  }).sort((a,b) => a.sortKey.localeCompare(b.sortKey))
                });
              }
            },
            {
              label: 'Sections', color: '#009FE3',
              value: hierarchy.filter(h => h.section).length,
              onClick: () => {
                setExpandedStatRows(new Set());
                setStatFilter({
                  type: 'section',
                  title: 'All Sections',
                  color: '#009FE3',
                  items: hierarchy.filter(h => h.section).map(h => ({
                    name: fullPath(h),
                    count: 1
                  })).sort((a, b) => a.name.localeCompare(b.name))
                });
              }
            },
          ].map(s => (
            <div key={s.label} onClick={s.onClick} style={{
              background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
              borderRadius: '12px', padding: '20px',
              borderTop: `4px solid ${s.color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s'
            }}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
            >
              <p style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: s.color }}>{s.value}</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#888' }}>{s.label}</p>
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: s.color, opacity: 0.7 }}>Click to view →</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', padding: '8px', borderRadius: '12px', width: 'fit-content', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {tabBtn('view', '📋 View All')}
          {tabBtn('add', '➕ Add Entry')}
          {tabBtn('upload', '📤 CSV Upload')}
        </div>

        {/* VIEW TAB */}
        {activeTab === 'view' && (
          <div style={{ background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                All Hierarchy Entries ({filtered.length})
              </h3>
              <input
                placeholder="🔍 Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ ...inp, width: '250px' }}
              />
            </div>

            {filtered.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#444', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={visibleRows.length > 0 && visibleRows.every((h) => selectedRows.has(h._idx))}
                    onChange={(e) => {
                      setSelectedRows(e.target.checked ? new Set(visibleRows.map((h) => h._idx)) : new Set());
                    }}
                  />
                  Select all visible
                </label>
                {selectedRows.size > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '13px', color: '#444' }}>{selectedRows.size} selected</span>
                    <button
                      onClick={() => setSelectedRows(new Set())}
                      disabled={saving}
                      style={{
                        background: 'none', color: '#666', border: '1px solid #D3D1C7',
                        padding: '5px 12px', borderRadius: '6px',
                        fontSize: '12px', cursor: 'pointer', fontWeight: '500'
                      }}>
                      Clear
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      disabled={saving}
                      style={{
                        background: '#DC2626', color: '#fff', border: 'none',
                        padding: '5px 14px', borderRadius: '6px',
                        fontSize: '12px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '600',
                        opacity: saving ? 0.6 : 1,
                      }}>
                      {saving ? 'Deleting…' : 'Delete Selected'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {Object.keys(grouped).length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>No entries found</p>
            ) : (
              Object.entries(grouped).map(([key, entries]) => (
                <div key={key} style={{ marginBottom: '16px' }}>
                  <div style={{
                    background: '#E8F4FD', padding: '8px 14px', borderRadius: '8px',
                    fontSize: '13px', fontWeight: '600', color: '#0072B1', marginBottom: '6px'
                  }}>
                    📍 {key}
                  </div>
                  <div style={{ paddingLeft: '8px' }}>
                    {entries.map((h) => (
                      <div key={h._idx} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', borderRadius: '6px', marginBottom: '4px',
                        background: '#F8FAFC', border: '1px solid #E5E7EB'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#444' }}>
                          <input
                            type="checkbox"
                            checked={selectedRows.has(h._idx)}
                            onChange={() => toggleRowSelected(h._idx)}
                          />
                          {h.circle
                            ? `${h.subdivision ? `${h.subdivision} → ` : ''}${h.section}`
                            : <em style={{ color: '#888' }}>Independent entry — no sub-hierarchy</em>}
                        </span>
                        <span style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button
                            onClick={() => openEdit(h)}
                            disabled={saving}
                            style={{
                              background: '#E8F4FD', color: '#0072B1', border: 'none',
                              padding: '4px 10px', borderRadius: '6px',
                              fontSize: '12px', cursor: 'pointer', fontWeight: '500'
                            }}>
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(h._idx)}
                            disabled={saving}
                            style={{
                              background: '#FEE2E2', color: '#DC2626', border: 'none',
                              padding: '4px 10px', borderRadius: '6px',
                              fontSize: '12px', cursor: 'pointer', fontWeight: '500'
                            }}>
                            Delete
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ADD TAB */}
        {activeTab === 'add' && (
          <div style={{ background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', borderRadius: '16px', padding: '32px', maxWidth: '600px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600' }}>Add New Entry</h3>

            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px',
              padding: '10px 14px', background: '#F8FAFC', border: '1px solid #E5E7EB',
              borderRadius: '8px', fontSize: '13px', color: '#444', cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={standalone}
                onChange={(e) => { setStandalone(e.target.checked); setForm(emptyForm); }}
              />
              Independent entry — not part of the Zone → Circle → Division → Sub-Division → Section chain
              (e.g. "BESCOM Corporate Office", "DAS, BESCOM")
            </label>

            <form onSubmit={handleAdd}>
              {standalone ? (
                <div style={{ marginBottom: '24px' }}>
                  <label style={lbl}>Name *</label>
                  <input name="zone" value={form.zone} onChange={handleChange} placeholder="e.g. BESCOM Corporate Office" required style={inp} />
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={lbl}>Zone *</label>
                      <HierarchyFieldInput name="zone" value={form.zone} onChange={handleChange} options={addOptions.zones} listId="hadd-zone" placeholder="e.g. BMASZ" required />
                    </div>
                    <div>
                      <label style={lbl}>Circle *</label>
                      <HierarchyFieldInput name="circle" value={form.circle} onChange={handleChange} options={addOptions.circles} listId="hadd-circle" placeholder="e.g. South Circle" required />
                    </div>
                    <div>
                      <label style={lbl}>Division *</label>
                      <HierarchyFieldInput name="division" value={form.division} onChange={handleChange} options={addOptions.divisions} listId="hadd-division" placeholder="e.g. Jayanagar Division" required />
                    </div>
                    <div>
                      <label style={lbl}>Sub-Division</label>
                      <HierarchyFieldInput name="subdivision" value={form.subdivision} onChange={handleChange} options={addOptions.subdivisions} listId="hadd-subdivision" placeholder="e.g. BTM Layout Sub-division" />
                    </div>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={lbl}>Section *</label>
                    <HierarchyFieldInput name="section" value={form.section} onChange={handleChange} options={addOptions.sections} listId="hadd-section" placeholder="e.g. BTM 1st Stage O&M" required />
                  </div>
                </>
              )}
              <button type="submit" disabled={saving} style={{
                padding: '12px 32px', background: '#009FE3', color: '#fff',
                border: 'none', borderRadius: '8px', fontSize: '15px',
                fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1
              }}>
                {saving ? 'Saving...' : 'Add Entry'}
              </button>
            </form>
          </div>
        )}

        {/* CSV UPLOAD TAB */}
        {activeTab === 'upload' && (
          <div style={{ background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', borderRadius: '16px', padding: '32px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600' }}>CSV Upload</h3>
            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 16px' }}>
              CSV must have headers: <code>zone,circle,division,subdivision,section</code>
            </p>

            {/* Download template */}
            <a
              href={`data:text/csv;charset=utf-8,zone,circle,division,subdivision,section\nBMASZ,South Circle,Jayanagar Division,BTM Layout Sub-division,BTM 1st Stage O%26M`}
              download="hierarchy_template.csv"
              style={{
                display: 'inline-block', marginBottom: '20px',
                padding: '8px 16px', background: '#E8F4FD',
                color: '#0072B1', borderRadius: '6px',
                fontSize: '13px', fontWeight: '500', textDecoration: 'none'
              }}>
              📥 Download Template
            </a>

            <div style={{ marginBottom: '20px' }}>
              <input type="file" accept=".csv" onChange={handleCSVUpload}
                style={{ display: 'block', fontSize: '14px' }} />
            </div>

            {csvPreview.length > 0 && (
              <>
                <div style={{ marginBottom: '16px', background: '#F0FDF4', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#15803D' }}>
                  ✅ {csvPreview.length} entries ready to import
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#F8FAFC' }}>
                        {['Zone', 'Circle', 'Division', 'Sub-Division', 'Section'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600', color: '#555' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.slice(0, 20).map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '6px 12px' }}>{row.zone}</td>
                          <td style={{ padding: '6px 12px' }}>{row.circle}</td>
                          <td style={{ padding: '6px 12px' }}>{row.division}</td>
                          <td style={{ padding: '6px 12px' }}>{row.subdivision}</td>
                          <td style={{ padding: '6px 12px' }}>{row.section}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvPreview.length > 20 && (
                    <p style={{ padding: '8px 12px', margin: 0, fontSize: '12px', color: '#888' }}>
                      ... and {csvPreview.length - 20} more entries
                    </p>
                  )}
                </div>
                <button onClick={handleCSVImport} disabled={saving} style={{
                  padding: '12px 32px', background: '#0F6E56', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '15px',
                  fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1
                }}>
                  {saving ? 'Importing...' : `Import ${csvPreview.length} Entries`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      </div>
      </div>

      {/* Rendered outside `page`/`card` — those have backdrop-filter
          (blur) on them, and any ancestor with backdrop-filter/transform
          creates a new containing block for `position: fixed` children,
          which broke this modal's centering (it ended up positioned
          relative to the tall, scrollable card instead of the viewport,
          landing far down the page instead of centered on screen). */}
      {statFilter && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setStatFilter(null)}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '32px',
            width: '90%', maxWidth: '600px', maxHeight: '80vh',
            overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: statFilter.color }}>
                {statFilter.title}
              </h3>
              <button onClick={() => setStatFilter(null)} style={{
                background: '#F3F4F6', border: 'none', borderRadius: '8px',
                padding: '6px 12px', cursor: 'pointer', fontSize: '16px'
              }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {statFilter.items.map((item, i) => {
                // Sections has no `children` at all (it's already the leaf
                // level — nothing to expand into) — not expandable.
                const expandable = Array.isArray(item.children);
                const isOpen = expandable && expandedStatRows.has(item.name);
                return (
                  <div key={i} style={{
                    background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                  }}>
                    <div
                      onClick={expandable ? () => toggleStatRow(item.name) : undefined}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 14px', cursor: expandable ? 'pointer' : 'default', gap: '10px',
                      }}>
                      <span style={{ fontSize: '14px', color: '#333', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {expandable && (
                          <span style={{
                            display: 'inline-block', fontSize: '11px', color: '#888',
                            transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease',
                          }}>▶</span>
                        )}
                        {item.name}
                      </span>
                      <span style={{
                        fontSize: '12px', color: '#fff', background: statFilter.color,
                        padding: '2px 10px', borderRadius: '12px', fontWeight: '600', flexShrink: 0,
                      }}>{item.count} sections</span>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '0 14px 10px 36px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {item.children.map((h, ci) => (
                          <div key={ci} style={{ fontSize: '13px', color: '#5F5E5A' }}>
                            {childPath(h, statFilter.type)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {/* Edit modal — same field layout as the Add Entry tab, pre-filled
          from the row's current values, so changing a Section's own
          Division/Circle/Zone/Sub-Division (not just its own name) is a
          normal edit instead of needing a delete-and-re-add round trip.
          Rendered at the same top level as the statFilter modal above,
          for the same reason (backdrop-filter containing-block issue). */}
      {editIdx !== null && (
        <div style={modalOverlay} onClick={closeEdit}>
          <div style={{ ...modalCard, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>
              Edit Entry
            </h3>
            <form onSubmit={handleEditSave}>
              {editStandalone ? (
                <div style={{ marginBottom: '24px' }}>
                  <label style={lbl}>Name *</label>
                  <input name="zone" value={editForm.zone} onChange={handleEditChange} placeholder="e.g. BESCOM Corporate Office" required style={inp} />
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={lbl}>Zone *</label>
                      <HierarchyFieldInput name="zone" value={editForm.zone} onChange={handleEditChange} options={editOptions.zones} listId="hedit-zone" placeholder="e.g. BMASZ" required />
                    </div>
                    <div>
                      <label style={lbl}>Circle *</label>
                      <HierarchyFieldInput name="circle" value={editForm.circle} onChange={handleEditChange} options={editOptions.circles} listId="hedit-circle" placeholder="e.g. South Circle" required />
                    </div>
                    <div>
                      <label style={lbl}>Division *</label>
                      <HierarchyFieldInput name="division" value={editForm.division} onChange={handleEditChange} options={editOptions.divisions} listId="hedit-division" placeholder="e.g. Jayanagar Division" required />
                    </div>
                    <div>
                      <label style={lbl}>Sub-Division</label>
                      <HierarchyFieldInput name="subdivision" value={editForm.subdivision} onChange={handleEditChange} options={editOptions.subdivisions} listId="hedit-subdivision" placeholder="e.g. BTM Layout Sub-division" />
                    </div>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={lbl}>Section *</label>
                    <HierarchyFieldInput name="section" value={editForm.section} onChange={handleEditChange} options={editOptions.sections} listId="hedit-section" placeholder="e.g. BTM 1st Stage O&M" required />
                  </div>
                </>
              )}
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '20px',
                padding: '10px 14px', background: '#F8FAFC', border: '1px solid #E5E7EB',
                borderRadius: '8px', fontSize: '13px', color: '#444', cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={cascadeUpdate}
                  onChange={(e) => setCascadeUpdate(e.target.checked)}
                  style={{ marginTop: '2px' }}
                />
                <span>
                  Also update any existing accounts currently assigned to this exact entry — their own posting
                  will be changed to match the new values above. You'll see how many accounts before this commits.
                </span>
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={closeEdit}
                  style={{
                    flex: 1, padding: '12px', background: 'none', border: '1px solid #D3D1C7',
                    color: '#5F5E5A', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving || cascading} style={{
                  flex: 1, padding: '12px', background: '#009FE3', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '14px',
                  fontWeight: '600', cursor: (saving || cascading) ? 'not-allowed' : 'pointer',
                  opacity: (saving || cascading) ? 0.7 : 1
                }}>
                  {cascading ? 'Checking accounts...' : saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog dialog={cascadeConfirm} onCancel={() => setCascadeConfirm(null)} />
    </>
  );
}
